// frontend/src/pages/admin/LearningDevelopment/components/LevyOptimizationTab.js
// HR operational workqueue for construction levy optimization:
//   * At Risk     — workers whose CoreTrade / Multi-Skilling / Direct R1 / MBF
//                   registration is expired or expiring within 90 days (a lapse
//                   reverts them to the basic-skilled higher levy tier).
//   * Opportunity — basic-skilled workers already eligible to upgrade, plus workers
//                   whose verified certificate evidence suggests an HR review.
// Backed by the same snapshot the weekly Celery scan refreshes; rows deep-link to
// each worker's Skills Upgrading profile tab.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  ShieldAlert, Clock, ArrowUpRight, DollarSign, RefreshCw,
  ChevronRight, CheckCircle, Info, Activity, Wrench, Award, Sparkles,
} from 'lucide-react';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import { InfoBadge } from '../../../../components/UI/InfoBadge';
import { CountBadge } from '../../../../components/UI/CountBadge';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import IconButton from '../../../../components/UI/IconButton';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';
import PillSelect from '../../../../components/UI/PillSelect';
import { Pagination } from '../../../../components/UI/Pagination';
import {
  formatDate,
  formatDateTimeCompact,
  formatExpiryUrgencyLabel,
  parseLocalDate,
} from '../../../../utils/dateUtils';
import { useLevyOptimization } from '../../../../hooks/useSkillsUpgradingData';

const PATHWAY_OPTIONS = [
  { value: 'all', label: 'All pathways' },
  { value: 'coretrade', label: 'CoreTrade' },
  { value: 'multi_skilling', label: 'Multi-Skilling' },
  { value: 'direct_r1', label: 'Direct R1' },
  { value: 'mbf', label: 'MBF' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Expired + expiring' },
  { value: 'expired', label: 'Expired only' },
  { value: 'expiring_soon', label: 'Expiring soon only' },
];

// Client-side page size — full snapshot is already in memory (same pattern as
// TrainingRecordsTab / CourseRequestsTab sibling tabs).
const PAGE_SIZE = 15;

/** Date-only expiry label for row chips; null when missing/invalid. */
const formatExpiry = (value) => {
  if (!value) return null;
  const localDate = parseLocalDate(String(value).split('T')[0]);
  if (!localDate) return null;
  const formatted = formatDate(localDate);
  return formatted === 'N/A' || formatted === 'Invalid Date' ? null : formatted;
};

const workerProfileLink = (staffId, returnTo) => {
  const base = `/admin/employees?employee=${encodeURIComponent(staffId)}&tab=skills-upgrading`;
  if (!returnTo) return base;
  return `${base}&returnTo=${encodeURIComponent(returnTo)}&returnLabel=${encodeURIComponent('Levy Optimization')}`;
};

/** Join non-empty context parts for the muted sub-line beneath a worker name. */
const formatWorkerMeta = (...parts) => parts.filter(Boolean).join(' · ');

/** Primary display name; avoids duplicating staff_id when name is unavailable. */
const workerDisplayName = (row) => {
  if (row.worker_name && row.worker_name !== row.staff_id) return row.worker_name;
  return row.staff_id;
};

/** Muted sub-line: staff ID only when it isn't already the primary label. */
const workerMetaLine = (row, ...extra) => formatWorkerMeta(
  row.worker_name && row.worker_name !== row.staff_id ? row.staff_id : null,
  row.department,
  row.designation,
  ...extra,
);

const requiresEvidenceAction = (row) => row.opportunity_status === 'certificate_review';

/** Missing-certification labels for a training-gap row's top likely pathway. */
const missingCertLabels = (row) => (row.missing_certifications || [])
  .map((cert) => cert.certification_short_name || cert.certification_name)
  .filter(Boolean);

// ── Metric tile ───────────────────────────────────────────────────────

const MetricTile = ({ icon: Icon, color, value, label, sub }) => (
  <div className="metrics-card">
    <div className="metrics-card-content">
      <div className={`metrics-card-icon metrics-card-icon-${color}`}>
        <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
      </div>
      <div className="metrics-card-text">
        <p className="metrics-card-value">{value}</p>
        <p className="metrics-card-label">{label}</p>
        {sub && <p className="text-[11px] sm:text-xs text-gray-400 truncate">{sub}</p>}
      </div>
    </div>
  </div>
);

// ── At-risk row ───────────────────────────────────────────────────────

const AtRiskRow = ({ row, returnTo }) => (
  // Stacks on mobile so the worker name keeps the full row width — a single
  // justify-between line lets the two right-hand pills crush the name to one
  // letter on a phone. Horizontal from sm up.
  <Link
    to={workerProfileLink(row.staff_id, returnTo)}
    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 sm:px-6 py-3 border border-gray-200 rounded-xl hover:ring-2 hover:ring-red-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 transition-all"
  >
    <div className="min-w-0 sm:flex-1">
      <p className="text-sm font-medium text-gray-900 truncate">
        {workerDisplayName(row)}
      </p>
      <p className="text-xs text-gray-500 truncate">
        {workerMetaLine(row, row.pathway_label, row.registered_trade)}
      </p>
      {row.has_cet_renewal_evidence && (
        <p className="mt-1 flex items-center gap-1 text-xs text-blue-700 truncate">
          <Award className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">
            Verified CET evidence: {[...new Set(row.cet_renewal_evidence.map(
              (certificate) => certificate.certificate_short_name || certificate.certificate_name,
            ).filter(Boolean))].join(', ')}
          </span>
        </p>
      )}
    </div>
    <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0 sm:justify-end sm:ml-4">
      {row.has_cet_renewal_evidence && (
        <StatusBadge status="pending_review" label="Renewal evidence review" />
      )}
      <span className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
        {row.validity === 'expired' ? 'Expired' : 'Expires'} {formatExpiry(row.expiry_date)}
      </span>
      <StatusBadge
        status={row.validity}
        label={formatExpiryUrgencyLabel(row.days_until_expiry) || undefined}
      />
      <ChevronRight className="w-4 h-4 text-gray-400 hidden sm:block" />
    </div>
  </Link>
);

// ── Opportunity row ───────────────────────────────────────────────────

const OpportunityRow = ({ row, returnTo }) => (
  <Link
    to={workerProfileLink(row.staff_id, returnTo)}
    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 sm:px-6 py-3 border border-gray-200 rounded-xl hover:ring-2 hover:ring-green-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 transition-all"
  >
    <div className="min-w-0 sm:flex-1">
      <p className="text-sm font-medium text-gray-900 truncate">
        {workerDisplayName(row)}
      </p>
      <p className="text-xs text-gray-500 truncate">
        {workerMetaLine(
          row,
          row.source_country,
          requiresEvidenceAction(row)
            ? `${row.suggested_pathways?.length || 0} suggested pathway${row.suggested_pathways?.length !== 1 ? 's' : ''}`
            : row.eligible_pathways_count
            ? `${row.eligible_pathways_count} eligible pathway${row.eligible_pathways_count !== 1 ? 's' : ''}`
            : null,
          row.recommended_pathway,
        )}
      </p>
      {requiresEvidenceAction(row) && row.matched_certificates?.length > 0 && (
        <p className="mt-1 flex items-center gap-1 text-xs text-blue-700 truncate">
          <Award className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">
            Verified evidence: {[...new Set(row.matched_certificates.map(
              (certificate) => certificate.certificate_short_name || certificate.certificate_name,
            ).filter(Boolean))].join(', ')}
          </span>
        </p>
      )}
      {row.levy_bill && (
        <p className="mt-1 flex items-center gap-1 text-xs text-gray-500 truncate">
          <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">
            MOM levy bill: {row.levy_bill.skill_level}
            {row.levy_bill.monthly_rate ? ` · $${row.levy_bill.monthly_rate.toLocaleString()}/mo` : ''}
            {row.levy_bill.levy_period ? ` (${row.levy_bill.levy_period})` : ''}
          </span>
        </p>
      )}
      {row.opportunity_status === 'training_gap' && row.likely_pathways?.length > 0 && (
        <p className="mt-1 flex items-center gap-1 text-xs text-indigo-700 truncate">
          <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">
            {row.current_skill_level ? (
              <>
                Likely pathway: {row.likely_pathways[0].pathway_label}
                {missingCertLabels(row).length > 0 && (
                  <> — missing: {missingCertLabels(row).join(', ')}</>
                )}
              </>
            ) : (
              <>
                Likely pathway: {row.likely_pathways[0].pathway_label}
                {row.prescreen?.experience_years
                  ? ` — ${row.prescreen.experience_years} yrs construction experience`
                  : ''}
                {' '}(case opens automatically)
              </>
            )}
            {row.likely_pathways.length > 1 && (
              <span className="text-gray-400">
                {` · +${row.likely_pathways.length - 1} more pathway${row.likely_pathways.length > 2 ? 's' : ''} pass experience/salary/nationality gates`}
              </span>
            )}
          </span>
        </p>
      )}
    </div>
    <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0 sm:justify-end sm:ml-4">
      {row.opportunity_status === 'certificate_review' && (
        <StatusBadge status="pending_review" label="HR confirmation required" />
      )}
      {row.opportunity_status === 'training_gap' && (
        <StatusBadge status="training_gap" />
      )}
      {row.opportunity_status === 'training_gap' && !row.current_skill_level && row.likely_pathways?.length > 0 && (
        <InfoBadge
          color="indigo"
          label="Pre-screened match"
          title="Objective screen only (experience, salary, nationality) — the case opens and the real engine runs automatically within minutes"
        />
      )}
      {row.potential_monthly_levy_savings > 0 && (
        <StatusBadge
          status={row.opportunity_status === 'certificate_review' ? 'pending_review' : 'eligible'}
          label={`${row.opportunity_status === 'certificate_review' ? 'Potential ' : 'Save '}$${row.potential_monthly_levy_savings.toLocaleString()}/mo`}
        />
      )}
      {!row.potential_monthly_levy_savings && row.levy_bill?.monthly_rate > 0 && (
        <span className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
          Billed ${row.levy_bill.monthly_rate.toLocaleString()}/mo
        </span>
      )}
      <ChevronRight className="w-4 h-4 text-gray-400 hidden sm:block" />
    </div>
  </Link>
);

// ── Main tab ──────────────────────────────────────────────────────────

const LevyOptimizationTab = () => {
  const { data: snapshot, isLoading, isError, isFetching, refetch } = useLevyOptimization();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Deep-link support: ?view=opportunities and ?status=expired|expiring_soon
  const initialView = searchParams.get('view') === 'opportunities' ? 'opportunities' : 'at_risk';
  const initialStatus = ['expired', 'expiring_soon'].includes(searchParams.get('status'))
    ? searchParams.get('status') : 'all';

  const [view, setView] = useState(initialView);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [pathwayFilter, setPathwayFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Round-trip: rows deep-link into the worker's Skills Upgrading profile. We
  // carry the current sub-view (At Risk / Opportunities + status) so closing
  // that profile returns HR to exactly the workqueue position they left.
  const returnTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', 'levy-optimization');
    params.set('view', view);
    if (view === 'at_risk' && statusFilter !== 'all') {
      params.set('status', statusFilter);
    } else {
      params.delete('status');
    }
    return `${location.pathname}?${params.toString()}`;
  }, [location.pathname, location.search, view, statusFilter]);

  const atRisk = useMemo(() => snapshot?.at_risk || [], [snapshot]);
  const opportunities = useMemo(() => snapshot?.opportunities || [], [snapshot]);
  const summary = snapshot?.summary;

  const filteredAtRisk = useMemo(() => {
    const term = search.trim().toLowerCase();
    return atRisk.filter((r) => {
      if (statusFilter !== 'all' && r.validity !== statusFilter) return false;
      if (pathwayFilter !== 'all' && r.pathway !== pathwayFilter) return false;
      if (term) {
        const haystack = [
          r.staff_id,
          r.worker_name,
          r.department,
          r.designation,
          r.pathway_label,
          r.registered_trade,
          ...(r.cet_renewal_evidence || []).flatMap((certificate) => [
            certificate.certificate_name,
            certificate.certificate_short_name,
          ]),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [atRisk, statusFilter, pathwayFilter, search]);

  const filteredOpportunities = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return opportunities;
    return opportunities.filter((o) => {
      const haystack = [
        o.staff_id,
        o.worker_name,
        o.department,
        o.designation,
        o.source_country,
        o.recommended_pathway,
        ...(o.suggested_pathways || []).map((pathway) => pathway.pathway_label),
        ...(o.matched_certificates || []).flatMap((certificate) => [
          certificate.certificate_name,
          certificate.certificate_short_name,
        ]),
        o.levy_bill?.skill_level,
        o.levy_bill?.levy_period,
        ...(o.likely_pathways || []).map((pathway) => pathway.pathway_label),
        o.prescreen?.source_country,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [opportunities, search]);

  // Reset to page 1 whenever the active list or its filters change so HR never
  // lands on an empty page after narrowing the queue.
  useEffect(() => {
    setCurrentPage(1);
  }, [view, statusFilter, pathwayFilter, search]);

  const activeRows = view === 'at_risk' ? filteredAtRisk : filteredOpportunities;
  const totalCount = activeRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  // Clamp in case filters shrink the list while HR is mid-page.
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return activeRows.slice(start, start + PAGE_SIZE);
  }, [activeRows, safePage]);

  if (isLoading) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
        <Info className="w-5 h-5 text-yellow-600 flex-shrink-0" />
        <p className="text-sm font-medium text-yellow-800">
          Unable to load levy-optimization data. This view is restricted to HR administrators.
        </p>
      </div>
    );
  }

  const scanTime = snapshot?.generated_at
    ? formatDateTimeCompact(snapshot.generated_at)
    : null;
  const scanLabel = scanTime && scanTime !== 'N/A' && scanTime !== 'Invalid DateTime'
    ? scanTime
    : null;

  return (
    <div className="space-y-6">
      {/* Intro + refresh — caption orients the workqueue; tiles/rows teach the queues */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
            Levy Optimization
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5 line-clamp-2">
            Workers at risk of losing their lowered levy, and those eligible to move to a lower levy
          </p>
          <p className="text-[11px] sm:text-xs text-gray-400 mt-1 truncate">
            Refreshes on certificate changes and weekly
            {scanLabel ? ` · updated ${scanLabel}` : ''}
          </p>
        </div>
        <IconButton
          icon={RefreshCw}
          loading={isFetching}
          onClick={() => refetch()}
          title="Refresh"
          ariaLabel="Refresh"
          className="flex-shrink-0"
        />
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <MetricTile
          icon={ShieldAlert}
          color={summary?.at_risk_total > 0 ? 'red' : 'green'}
          value={summary?.at_risk_total ?? 0}
          label="At Risk"
          sub={[
            `${summary?.at_risk_expired ?? 0} expired`,
            `${summary?.at_risk_expiring_soon ?? 0} expiring`,
            summary?.at_risk_cet_evidence_review > 0
              ? `${summary.at_risk_cet_evidence_review} CET review`
              : null,
          ].filter(Boolean).join(' · ')}
        />
        <MetricTile
          icon={Clock}
          color={summary?.at_risk_expiring_soon > 0 ? 'orange' : 'green'}
          value={summary?.at_risk_expiring_soon ?? 0}
          label="Expiring ≤90 days"
        />
        <MetricTile
          icon={ArrowUpRight}
          color="blue"
          value={summary?.opportunities_total ?? 0}
          label="Upgrade Opportunities"
          sub={[
            `${summary?.opportunities_certificate_review ?? 0} certificate review`,
            `${summary?.opportunities_training_gap ?? 0} training gap`,
            summary?.opportunities_likely_eligible > 0
              ? `${summary.opportunities_likely_eligible} likely eligible`
              : null,
          ].filter(Boolean).join(' · ')}
        />
        <MetricTile
          icon={DollarSign}
          color="green"
          value={`$${(summary?.opportunities_annual_savings ?? 0).toLocaleString()}`}
          label="Potential Savings/yr"
          sub={summary?.opportunities_monthly_savings > 0 ? `$${summary.opportunities_monthly_savings.toLocaleString()}/mo` : null}
        />
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
        <button
          onClick={() => setView('at_risk')}
          className={`tab-btn-admin tab-btn-admin-sm ${view === 'at_risk' ? 'active' : ''}`}
        >
          <ShieldAlert className="w-4 h-4" />
          At Risk
          {atRisk.length > 0 && <CountBadge count={atRisk.length} theme="admin" active={view === 'at_risk'} />}
        </button>
        <button
          onClick={() => setView('opportunities')}
          className={`tab-btn-admin tab-btn-admin-sm ${view === 'opportunities' ? 'active' : ''}`}
        >
          <ArrowUpRight className="w-4 h-4" />
          Upgrade Opportunities
          {opportunities.length > 0 && <CountBadge count={opportunities.length} theme="admin" active={view === 'opportunities'} />}
        </button>
      </div>

      {/* Filters — search grows on the left; pill filters stay clustered (no w-full on
          PillSelect in a flex row — that stretches each wrapper and blows them apart). */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 max-w-xl min-w-0">
          <DebouncedSearchInput
            value={search}
            onSearch={setSearch}
            placeholder="Search by name, staff ID, department, or trade"
            theme="admin"
          />
        </div>
        {view === 'at_risk' && (
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <PillSelect
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              icon={Activity}
              iconColor="text-red-600"
              theme="admin"
            />
            <PillSelect
              options={PATHWAY_OPTIONS}
              value={pathwayFilter}
              onChange={setPathwayFilter}
              icon={Wrench}
              iconColor="text-red-600"
              theme="admin"
            />
          </div>
        )}
      </div>

      {/* Lists + shared DS Pagination (client-side — full snapshot already loaded) */}
      {totalCount > 0 ? (
        <div className="space-y-2">
          {view === 'at_risk'
            ? pageRows.map((r, idx) => (
              <AtRiskRow key={`${r.staff_id}-${r.pathway}-${idx}`} row={r} returnTo={returnTo} />
            ))
            : pageRows.map((o, idx) => (
              <OpportunityRow key={`${o.staff_id}-${idx}`} row={o} returnTo={returnTo} />
            ))}
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            theme="admin"
            noun={view === 'at_risk' ? 'workers' : 'opportunities'}
            className="!rounded-2xl"
          />
        </div>
      ) : (
        <EmptyState
          label={view === 'at_risk'
            ? (atRisk.length === 0
              ? 'No workers are at risk of losing their lowered levy.'
              : 'No workers match the current filters.')
            : (opportunities.length === 0
              ? 'No upgrade opportunities right now.'
              : 'No workers match the current filters.')}
        />
      )}
    </div>
  );
};

const EmptyState = ({ label }) => (
  <div className="py-12 text-center">
    <CheckCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
    <p className="text-sm font-medium text-gray-500">{label}</p>
  </div>
);

export default LevyOptimizationTab;
